import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { FaLinkedinIn, FaXTwitter, FaEnvelope } from "react-icons/fa6";
import { FaShieldAlt } from "react-icons/fa";

const footerLinks = [
  { label: "About Us", to: "/about" },
  { label: "FAQs", to: "/faqs" },
  { label: "Contact Us", to: "/contact" },
  { label: "Terms & Conditions", to: "/terms" },
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Refund Policy", to: "/refund-policy" },
  { label: "Disclaimer", to: "/disclaimer" },
];

const SiteFooter = () => {
  const [linkedinUrl, setLinkedinUrl] = useState("https://www.linkedin.com/company/mooltax");
  const [twitterUrl, setTwitterUrl] = useState("https://x.com/mooltax");

  useEffect(() => {
    // Fetch social links from public config endpoint
    api.get("/config/public").then(res => {
      if (res.data?.linkedin_url) setLinkedinUrl(res.data.linkedin_url);
      if (res.data?.twitter_url) setTwitterUrl(res.data.twitter_url);
    }).catch(() => {}); // fail silently — fallback to defaults
  }, []);

  return (
    <footer
      className="px-6 pt-12 pb-8 border-t"
      style={{
        borderColor: "var(--border)",
        backgroundColor: "var(--background)",
      }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Brand / Description */}
          <div className="lg:col-span-5">
            <h3
              className="text-3xl font-bold mb-4"
              style={{ fontFamily: "Fraunces, serif", color: "var(--primary)" }}
            >
              MoolTax
            </h3>

            <p
              className="text-base leading-8 max-w-xl"
              style={{ color: "var(--foreground)", opacity: 0.75 }}
            >
              A guided tax filing support platform designed to make document upload,
              case tracking, and expert coordination simpler and more transparent.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <div
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium"
                style={{
                  backgroundColor: "var(--secondary)",
                  color: "var(--primary)",
                }}
              >
                <FaShieldAlt className="text-sm" />
                Secure & trust-focused support
              </div>
            </div>
          </div>

          {/* Links */}
          <div className="lg:col-span-4">
            <h4
              className="text-sm font-semibold uppercase tracking-[0.18em] mb-4"
              style={{ color: "var(--foreground)", opacity: 0.7 }}
            >
              Quick Links
            </h4>

            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {footerLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-sm transition-all duration-200 hover:translate-x-0.5"
                  style={{ color: "var(--foreground)" }}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Connect */}
          <div className="lg:col-span-3">
            <h4
              className="text-sm font-semibold uppercase tracking-[0.18em] mb-4"
              style={{ color: "var(--foreground)", opacity: 0.7 }}
            >
              Connect
            </h4>

            <div className="flex items-center gap-3">
              <a
                href={linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="MoolTax on LinkedIn"
                className="flex h-11 w-11 items-center justify-center rounded-full border shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
                style={{
                  borderColor: "var(--border)",
                  backgroundColor: "var(--background)",
                  color: "var(--foreground)",
                }}
              >
                <FaLinkedinIn className="text-[17px]" />
              </a>

              <a
                href={twitterUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="MoolTax on X"
                className="flex h-11 w-11 items-center justify-center rounded-full border shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
                style={{
                  borderColor: "var(--border)",
                  backgroundColor: "var(--background)",
                  color: "var(--foreground)",
                }}
              >
                <FaXTwitter className="text-[17px]" />
              </a>

              <a
                href="mailto:support@mooltax.com"
                aria-label="Email MoolTax support"
                className="flex h-11 w-11 items-center justify-center rounded-full border shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
                style={{
                  borderColor: "var(--border)",
                  backgroundColor: "var(--background)",
                  color: "var(--foreground)",
                }}
              >
                <FaEnvelope className="text-[16px]" />
              </a>
            </div>

            <div className="mt-4 space-y-2">
              <p
                className="text-sm"
                style={{ color: "var(--foreground)", opacity: 0.7 }}
              >
                support@mooltax.com
              </p>
              <p
                className="text-xs leading-6"
                style={{ color: "var(--foreground)", opacity: 0.55 }}
              >
                Follow us for updates and support.
              </p>
            </div>
          </div>
        </div>

        <div
          className="mt-10 pt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          style={{ borderTop: "1px solid var(--border)" }}
        >
          <p
            className="text-sm"
            style={{ color: "var(--foreground)", opacity: 0.6 }}
          >
            © {new Date().getFullYear()} MoolTax. All rights reserved.
          </p>

          <div className="flex flex-wrap items-center gap-4 text-sm">
            <Link
              to="/privacy"
              className="transition-opacity duration-200 hover:opacity-100"
              style={{ color: "var(--foreground)", opacity: 0.6 }}
            >
              Privacy
            </Link>
            <Link
              to="/terms"
              className="transition-opacity duration-200 hover:opacity-100"
              style={{ color: "var(--foreground)", opacity: 0.6 }}
            >
              Terms
            </Link>
            <Link
              to="/contact"
              className="transition-opacity duration-200 hover:opacity-100"
              style={{ color: "var(--foreground)", opacity: 0.6 }}
            >
              Contact
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;